import {
  ANCHOR_KEYWORD_PER_LESSON_FORMAT,
  TARGET_SECTION,
} from '@lessonPlanner/prompts';
import { REGEX } from '@lessonPlanner/regex';

import { Activity } from '../types/activity';
import { Section } from '../types/section';

export function extractActivityNumber(
  input: string,
  activityKeyword = TARGET_SECTION.ACTIVITY,
  activityKeywordFilipino = TARGET_SECTION.AKTIBIDAD,
): number | null {
  const colonSymbol = ':';

  const activityKeywordIndex = input.indexOf(activityKeyword);
  const activityKeywordIndexFilipino = input.indexOf(activityKeywordFilipino);
  if (activityKeywordIndex !== -1 || activityKeywordIndexFilipino !== -1) {
    const startIndex = activityKeywordIndex + activityKeyword.length;
    const colonIndex = input.indexOf(colonSymbol, startIndex);

    if (colonIndex !== -1) {
      const activityNumberString = input.substring(startIndex, colonIndex);
      const activityNumber = parseInt(activityNumberString);

      if (!isNaN(activityNumber)) {
        return activityNumber;
      }
    }
  }

  return null;
}

export function elaborateExploreSection5OR7E(
  input: string,
  stringToAdd: string,
  activityNum: number,
): string {
  const termRegex = REGEX.ISOLATE_SECTION_TITLE_5OR7ES;

  const match = input.match(termRegex);

  if (match && match[1]) {
    const exploreContent = match[1];

    const modifiedExploreContent = elaborateOnActivity(
      exploreContent,
      activityNum,
      stringToAdd,
    );

    const modifiedInput = input.replace(
      termRegex,
      `Explore:\n${modifiedExploreContent}`,
    );

    return modifiedInput;
  }

  return input;
}

export function elaborateActivitySection4A(
  input: string,
  stringToAdd: string,
  activityNum: number,
): string {
  const termRegex = REGEX.ISOLATE_SECTION_TITLE_5OR7ES;

  const match = input.match(termRegex);

  if (match && match[1]) {
    const activityContent = match[1];

    const modifiedActivityContent = elaborateOnActivity(
      activityContent,
      activityNum,
      stringToAdd,
    );

    const modifiedInput = input.replace(
      termRegex,
      `Activity:\n${modifiedActivityContent}`,
    );

    return modifiedInput;
  }

  return input;
}

export function modifyActivities(
  input: string,
  stringToAdd: string,
  targetSection: Section,
) {
  let identifier = findIdentifier(input, targetSection.sectionTitleEnglish);

  if (identifier == '') {
    identifier = findIdentifier(input, targetSection.sectionTitleFilipino);
  }

  if (identifier !== '') {
    const activityContent = extractContent(input, identifier);

    const modifiedActivityContent = addAdditionalActivities(
      activityContent,
      stringToAdd,
    );

    const modifiedInput = replaceContent(
      input,
      identifier,
      modifiedActivityContent,
      targetSection.sectionTitleEnglish,
    );

    return modifiedInput;
  }

  return input;
}

export function splitTextByPattern(
  text: string,
  lessonFormat: string,
): {
  beginning: string;
  end: string;
} {
  let identifier = '';

  const anchorKeywords =
    ANCHOR_KEYWORD_PER_LESSON_FORMAT.find(
      (item) => item.lessonFormat === lessonFormat,
    ).anchorKeywords || [];

  identifier = anchorKeywords.find((keyword) => text.includes(keyword)) || '';

  if (identifier === '') {
    // If no identifier is found, return the whole text as beginning and empty end
    return { beginning: text.trim(), end: '' };
  }

  const beginning = text.substring(0, text.indexOf(identifier)).trim();
  const end = text.substring(text.indexOf(identifier)).trim();
  if (!beginning) {
    return { beginning: text.trim(), end: '' };
  }

  return { beginning, end };
}

function findIdentifier(text: string, keyword: string): string {
  if (text.includes(keyword)) {
    return keyword;
  }

  // Keyword was not found in the text
  return '';
}

function extractContent(text: string, identifier: string): string {
  const index = text.indexOf(identifier);
  if (index !== -1) {
    return text.substring(index + identifier.length).trim();
  }
  return '';
}

function separateActivities(text: string): Activity[] {
  const activities: Activity[] = [];
  const activityRegex = /Activity (\d+): ([^\n]+)\n((?:- [^\n]+\n)+)/g;
  let match;

  while ((match = activityRegex.exec(text)) !== null) {
    const number = parseInt(match[1]);
    const title = match[2];
    const content = match[3].trim();

    activities.push({ number, title, content });
  }

  return activities;
}

function addAdditionalActivities(
  text: string,
  activitiesToAdd: string,
): string {
  const activities = separateActivities(text);
  const additionalActivities = separateActivities(activitiesToAdd);

  activities.push(...additionalActivities);

  activities.sort((a, b) => a.number - b.number);

  let updatedText = '';

  for (const activity of activities) {
    updatedText += `Activity ${activity.number}: ${activity.title}\n${activity.content}\n\n`;
  }

  return updatedText.trim();
}

function replaceContent(
  text: string,
  identifier: string,
  newContent: string,
  replacement: string,
): string {
  const index = text.indexOf(identifier);
  if (index !== -1) {
    const beginning = text.substring(0, index);
    const end = text.substring(index + identifier.length);
    return `${beginning}${replacement}\n${newContent}${end}`;
  }
  return text;
}

function elaborateOnActivity(
  text: string,
  activityNumber: number,
  newContent: string,
): string {
  const activities = separateActivities(text);

  const activityIndex = activities.findIndex(
    (activity) => activity.number === activityNumber,
  );

  if (activityIndex !== -1) {
    activities[activityIndex].content = newContent;
  }

  let updatedText = '';

  for (const activity of activities) {
    updatedText += `Activity ${activity.number}: ${activity.title}\n${activity.content}\n\n`;
  }

  return updatedText.trim();
}
